import { CompanyIndustry, JobLocation } from "interfaces/api/apiInterfaces";
import { months } from "./constants";

/**
 * Objects with unknown keys and values.
 */
 interface IMetadataObj {
    [key: string]: any;
}

/**
 * Convert a word to proper Case.
 */
const titleWordCase = (txt: string): string => `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`;

/**
 * Regex for non-alphanumerics and spaces.
 */
const regex = /([~!@#$%^&*\]){}_+\-(\s])/ig;

/**
 * Convert a sentence to proper Case.
 */
const titleCase = (txt: string): string => txt.split(regex).map(titleWordCase).join("");

/**
 * Convert number to string and add a comma for thousands and millions.
 */
const formatNumberComma = (value: string): string => {
    const numberVal = parseInt(value, 10);
    return Number.isNaN(numberVal) ? value : new Intl.NumberFormat("en-US").format(numberVal);
};

export const formatCompanySize = (size: string): string => {
    const sizes = size?.split("_").map((companySize) => formatNumberComma(companySize));
    return sizes && sizes.length && sizes[1] === "plus" ? `${sizes[0]}+` : sizes?.join(" - ");
};

export const getCompanyIndustries = (industries: CompanyIndustry[]) => industries?.map((industry) => industry.name).join(", ") ?? "";

/**
 * generate the canonical URL.
 */
const getCanonicalUrl = (asPath: string = "/"): string => {
    const preface = "https://www.themuse.com";
    const path = asPath.split("?")[0];
    return preface + path;
};

const parsePath = (urlPath: string) => urlPath.replace(/^\/(search\/)?/, "")
    .split("/").reduce((values, pathPart, index, shortenedPath): IMetadataObj => {
        const localValues = values;

        if (index % 2) {
            localValues[shortenedPath[index - 1]] = pathPart;
        } else {
            localValues[pathPart] = "";
        }

        return localValues;
    }, {} as IMetadataObj);

/**
 *  Rename the object names to query param names
 */
const objToQueryParams = (obj: IMetadataObj): string => {
    const mappedObj = Object.keys(obj).reduce((acc, key) => ({
        ...acc,
        ...{ [key]: encodeURIComponent(obj[key]) }
    }), {} as IMetadataObj);

    const queryParamStr = Object.keys(mappedObj)
        .filter((key) => {
            if (mappedObj[key] && mappedObj[key].length > 0) {
                return key;
            }
            return null;
        })
        .map((key: string) => `${key}/${mappedObj[key] || ""}`)
        .join("/");
    return queryParamStr;
};

/**
 * Convert the query string into an object
 */
const queryStringToObj = (searchString: string): IMetadataObj => {
    const pairs: string[] = searchString.split("&");
    const result: IMetadataObj = {};
    pairs.forEach((pair) => {
        const pairSplit: string[] = pair.split("=");
        if (pairSplit[0] in result) {
            // check for additional values and add it to the result[key]
            const splitValues = result[pairSplit[0]].split(",");
            splitValues.push(decodeURIComponent(pairSplit[1]));
            result[pairSplit[0]] = splitValues.join(",");
        } else {
            result[pairSplit[0]] = decodeURIComponent(pairSplit[1] || "");
        }
    });

    return result;
};

/**
 * Convert an object to query string removing nulls.
 */
const objToQueryString = (obj: IMetadataObj): string => Object.keys(obj)
    .map((key: string) => `${key}=${obj[key] || ""}`)
    .join("&");

/**
 * Convert a string to a slug.
 * * Decode URL encoded strings.
 * * Remove all white space with collapseWhiteSpace.
 * * Convert all diacritic to generics.
 * * Replace not alpha-numerics with dashes.
 * * Collapse all dashes to one dash.
 */
const slugify = (text: string): string => decodeURIComponent(collapseWhiteSpace(text))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/gmi, "-")
    .replace(/-+/g, "-");

/**
 * Returns a slugified location name.
 * Use this for location instead of `slugify` as this normalizes
 * the location slug for remote-flexible
 * @param locationName string
 * @returns string
 */
const slugifyLocation = (locationName: string): string => {
    if (locationName === "Flexible / Remote") {
        return "remote-flexible";
    }
    return slugify(locationName);
};

/**
 * Formats a date in correct format for job card
 */
const formatDate = (date: Date) => {
    const month = months[date.getMonth()];
    const day = date.getDate();

    return `${month} ${day}`;
};

/**
 * Returns a job apply redirect url
 */
const getApplyRedirectUrl = (jobId: number) => `/job/redirect/${jobId}`;

const getCompanyLogo = (companyLogo: string) => {
    const urlRegex = /^((http|https):\/\/)/i;
    return companyLogo?.length > 10 && companyLogo.match(urlRegex) ? companyLogo : "";
};

/**
 * Return the display content for locations
 * content: The items to display in the tooltip
 */
const getLocations = (locations: JobLocation[]) => {
    const remoteString = "Flexible / Remote";
    const suffix = "Remote";
    const strLocations = locations.filter((x) => x?.value).map((loc: JobLocation) => loc.value);
    const separator = " / ";
    const displaySuffix = strLocations.filter((value) => value === remoteString).length;
    const nonRemoteLocations = strLocations.filter((value) => value !== remoteString).slice(0, 37);
    const topLocation = nonRemoteLocations.shift();
    let dispLocation = "";

    if (displaySuffix) {
        dispLocation = suffix;
    }
    if (topLocation) {
        dispLocation = topLocation;
    }
    if (topLocation && displaySuffix) {
        dispLocation = `${topLocation} ${separator} ${suffix}`;
    }

    const triggerText = `(+${nonRemoteLocations.length} more)`;
    const result = nonRemoteLocations.length && {
        title: "locationsTip",
        triggerText,
        content: nonRemoteLocations
    };

    return {
        dispLocation,
        tooltipProps: result
    };
};

const truncateStringWithEllipsis = (targetString: string, truncateLength: number): string => targetString.length > truncateLength
    ? targetString.substr(0, truncateLength).concat("...") : targetString;

/**
 * Remove all contiguous whiteSpace,
 * * ' ' spaces.
 * * \n linefeed (LF or 0x0A (10) in ASCII).
 * * \r carriage return (CR or 0x0D (13) in ASCII).
 * * \t horizontal tab (HT or 0x09 (9) in ASCII).
 * * \v vertical tab (VT or 0x0B (11) in ASCII).
 * * \f form feed (FF or 0x0C (12) in ASCII).
 */
const collapseWhiteSpace = (content: string) => content
    .trim()
    .replace(/[ \n\r\t\v\f]+/gm, " "); // replace non-printing characters

/**
 * Returns true for "true", "True", "TRUE" and "1".
 * @param string string
 * @returns boolean
 */
const isTruthy = (string: string) => /^(true|1)$/.test(string.toLowerCase());

export {
    collapseWhiteSpace,
    formatDate,
    formatNumberComma,
    getApplyRedirectUrl,
    getCanonicalUrl,
    getCompanyLogo,
    getLocations,
    objToQueryParams,
    objToQueryString,
    parsePath,
    queryStringToObj,
    slugify,
    slugifyLocation,
    titleCase,
    truncateStringWithEllipsis,
    isTruthy
};

export type { IMetadataObj };
